import React, { Component } from "react";
import { RoutedViewProps } from "@my-dld/utils/UrlUtils";
import { Redirect } from "react-router";
import PageContent from "@my-dld/components/PageContent";
import Strings from "@my-dld/assets/strings";
import { DewaAccount, EjariProperty, Lookup, PropEditDataModel } from "@eres/ejari-api/Entity";
import { EjariContext } from "@eres/ejari-core/contexts/EjariContext";
import { trackEventWithData } from "@my-dld/utils/Analytics";
import FormSection from "@my-dld/components/Form/FormSection";
import FormRow from "@my-dld/components/Form/FormRow";
import { FormFieldProps } from "@my-dld/components/Form/FormField";
import { hideLoading, LoadableDataState, showErrorMessage, showLoading, showMessage } from "@my-dld/utils/UIUtils";
import EjariApi from "@eres/ejari-api";
import { IdExtractor, IdToTempLookup } from "@my-dld/utils/LookupUtil";
import { StringUtil } from "@eres/core";
import FormAction from "@my-dld/components/Form/FormAction";
import { getParticipantID } from "@10x/sso";
import { navigate } from "@my-dld/utils/StoreUtils";
import { extractPropInfo } from "../component/PropertyHeader";
import EjariPageTitle from "../component/EjariPageTitle";
import { UpdateProperty } from "@eres/ejari-core/actions";
import PickerInput from "@eres/forms-web/PickerInput";
import StateHolder from "@eres/forms/StateHolder";
import MultiListInput from "@eres/forms-web/MultiListInput";
import TextInputField from "@eres/forms-web/TextInputField";
import NumericStringConverter from "@eres/forms/converters/NumericStringConverter";
import CheckBoxField from "@eres/forms-web/CheckBoxField";
import { getAppServices } from "@10x/coreapp/index";

interface Props extends RoutedViewProps<any> {

}

interface LookupState {
	usages?: Lookup[];
	types?: Lookup[];
	subTypes?: Lookup[];
}

interface FormState {
	usage?: Lookup;
	type?: Lookup;
	subType?: Lookup;
	dewaAccounts?: DewaAccount[];
}

interface State extends LookupState, FormState, LoadableDataState {
	error?: {},
	propertyTypeId?: number;
}

class PropertyEdit extends Component<Props, State> {
	static contextType = EjariContext;
	context!: React.ContextType<typeof EjariContext>;

	constructor( props: Props ) {
		super( props );
		this.state = {
			loadState: "loading",
			errorContainer: {},
		};
	}

	componentDidMount() {
		if ( this.context.property ) {
			const property = this.context.property;
			trackEventWithData( "EjariProperty", "OpenEdit", {
				type: property.PropertyTypeId,
				id: property.PropertyId
			} );
			this.setState( {
				propertyTypeId: property.PropertyTypeId,
				usage: IdToTempLookup( property.EjariPropertyUsageId ),
				type: IdToTempLookup( property.EjariTypeId ),
				subType: IdToTempLookup( property.EjariSubTypeId ),
				dewaAccounts: property.AssociatedDewaAccounts,
			} );
			EjariApi.getPropertyUsages( property.PropertyId, property.IsFreeHold, property.PropertyTypeId )
			.then( usages => {
				if ( usages.length === 0 && property.EjariPropertyUsageId != null ) {
					usages.push( {
						Id: property.EjariPropertyUsageId,
						ar: property.EjariPropertyUsageId + "",
						en: property.EjariPropertyUsageId + "",
					} );
				}
				this.setState( { usages: usages, loadState: "done" } );
			} );
		}
	}

	componentDidUpdate( prevProps: Readonly<Props>, prevState: Readonly<State>, snapshot?: any ) {
		// usage handler
		const prevUsage = prevState?.usage;
		const currentUsage = this.state?.usage;
		if ( currentUsage ) {
			if ( prevUsage?.Id != currentUsage?.Id ) {
				this.updateTypes( currentUsage );
			}
		}
		// type handler
		const prevType = prevState?.type;
		const currentType = this.state?.type;
		if ( currentType && prevType?.Id != currentType?.Id ) {
			this.updateSubTypes( currentType );
		}
	}

	updateTypes = ( value: Lookup, extra?: any ) => {
		this.setState( { types: undefined, subTypes: undefined, loadState: "loading" } );
		EjariApi.getPropertyTypes( value.Id, this.state.propertyTypeId! )
		.then( types => {
			this.setState( { types: types, loadState: "done" } );
		} );
	};
	updateSubTypes = ( value: Lookup, extra?: any ) => {
		this.setState( { subTypes: undefined, loadState: "loading" } );
		EjariApi.getPropertySubTypes( value.Id, )
		.then( subTypes => {
			this.setState( { subTypes: subTypes, loadState: "done" } );
		} );
	};

	render() {
		const { property, tabuProperty } = this.context;
		const { usages, types, subTypes, dewaAccounts } = this.state;
		const isValid = this.isValid();
		if ( property == null )
			return <Redirect to={ "/mydld" }/>;
		const filedProps: Partial<FormFieldProps<any>> = {
			holder: StateHolder.buildStateHolder( this ),
			containerClassName: "col-md-4 form-group",
		};
		const { val, lbl } = extractPropInfo( property );
		return <PageContent
			title={ `${ Strings.edit_property_details } - ${ lbl } : ${ val }` }
			hideQLinks={ true }>
			<EjariPageTitle title={ Strings.edit_property_details }/>
			<React.Fragment>
				<FormSection title={ Strings.edit_property_details }>
					<div className="mandatory-field-info">
						<small>{ Strings.mandatory_fields_note }</small>
					</div>
					<FormRow { ...filedProps }>
						<PickerInput
							required={ true }
							isLoading={ usages === undefined }
							id={ "usage" }
							toIdentifier={ IdExtractor }
							disabled={ true }
							label={ Strings.usage } items={ usages }
						/>
					</FormRow>
					<FormRow { ...filedProps }>
						<PickerInput
							required={ true }
							isLoading={ usages === undefined || types === undefined }
							id={ "type" }
							disabled={ property.IsRented }
							toIdentifier={ IdExtractor }
							label={ Strings.property_type }
							items={ types }

						/>
						<PickerInput
							required={ true }
							isLoading={ usages === undefined || types === undefined || subTypes === undefined }
							disabled={ property.IsRented }
							id={ "subType" }
							toIdentifier={ IdExtractor }
							label={ Strings.property_sub_type } items={ subTypes }/>
					</FormRow>
					<FormRow { ...filedProps } containerClassName={ "col-md-12" }>
						<MultiListInput<DewaAccount>
							id={ "dewaAccounts" }
							required={ true }
							label={ Strings.dewa }
							renderForm={ ( inpPros => {
								return <DewaForm
									onNew={ this.onNewDewaAccount }
									current={ this.state.dewaAccounts }
								/>;
							} ) }
							renderListItem={ ( item: DewaAccount, index ) => {
								return <div key={ index }>
									<div>{ item.AccountNumber }</div>
									<div>{ Strings.is_common + " : " + ( item.Iscommon ? Strings.yes : Strings.no ) }</div>
								</div>;
							} }
						/>
					</FormRow>
				</FormSection>
				<FormRow>
					<FormAction
						disablePrimaryAction={ !isValid }
						primaryActionLabel={ Strings.update }
						secondaryActionLabel={ Strings.cancel }
						onSecondaryAction={ this.props.history.goBack }
						onPrimaryAction={ this.onUpdate }/>
				</FormRow>
			</React.Fragment>
		</PageContent>;
	}

	onUpdate = () => {
		const req: PropEditDataModel = {};
		const property = this.context.property!;
		req.PropertyTypeId = property.PropertyTypeId;
		req.ManagementCompanyId = property.ManagementCompanyId;
		req.PropertyId = property.PropertyId;
		req.ActiveUserID = "" + this.context.activeUserId;
		req.ActiveUserType = 4;
		req.PrimaryOwnerParticipantId = getParticipantID();
		req.HoldType = property.IsFreeHold;
		req.EjariPropertyId = property.EjariPropertyId;

		req.PropertyUsageId = this.state.usage?.Id;
		req.EjariPropertyTypeId = this.state.type?.Id;
		req.EjariPropertySubTypeId = this.state.subType?.Id;
		req.AssociatedDewaAccounts = this.state.dewaAccounts!.map( d => {
			return { Iscommon: d.Iscommon, AccountNumber: d.AccountNumber } as DewaAccount;
		} );
		showLoading();
		trackEventWithData( "Ejari", "UpdatePropertyTry", { ejariPropertyId: req.EjariPropertyId + "" } );
		EjariApi.updatePropertyDetail( req )
		.then( p => {
			hideLoading();
			UpdateProperty( getAppServices().storeService.getStore() );
			trackEventWithData( "Ejari", "UpdatePropertySuccess", { ejariPropertyId: req.EjariPropertyId + "" } );
			showMessage( Strings.property_update_success );
			const tabuProp = this.context.tabuProperty;
			if ( tabuProp ) navigate( `/mydld/property/${ tabuProp?.propertyTypeId }/${ tabuProp?.propertyId }/ejarisvc/details` );
		} )
		.catch( e => {
			trackEventWithData( "Ejari", "UpdatePropertyFail", { ejariPropertyId: req.EjariPropertyId + "" } );
			hideLoading();
			showErrorMessage( e );
		} );
	};
	isValid = () => {
		let isValid = true;
		isValid = isValid && this.state.usage != null;
		isValid = isValid && this.state.type != null;
		isValid = isValid && this.state.subTypes != null;
		isValid = isValid && this.state.dewaAccounts != null && this.state.dewaAccounts.length > 0;
		return isValid;
	};
	onNewDewaAccount = ( account: DewaAccount ) => {
		let accounts = this.state.dewaAccounts ? this.state.dewaAccounts : [];
		this.setState( {
			dewaAccounts: [
				...accounts,
				account,
			]
		} );
	};
}

interface DFProps {
	current?: DewaAccount[];
	onNew: ( account: DewaAccount ) => void;
}

interface DFState {
	isCommon?: boolean;
	AccountNumber?: string;
	error?: string;
	dKey: number;
}

class DewaForm extends Component<DFProps, DFState> {
	constructor( props: DFProps ) {
		super( props );
		this.state = {
			dKey: 1,
		};
	}

	onAdd = () => {
		this.setState( {
			error: undefined,
		} );
		const currentAccounts = this.props.current;
		if ( currentAccounts ) {
			const x = currentAccounts.find( a => a.AccountNumber === this.state.AccountNumber );
			if ( x ) {
				this.setState( { error: Strings.dewa_already_added } );
				return;
			}
		}
		const na: DewaAccount = {
			AccountNumber: this.state.AccountNumber!,
			Iscommon: this.state.isCommon
		};
		this.props.onNew( na );
		this.setState( {
			AccountNumber: undefined,
			isCommon: undefined,
			dKey: this.state.dKey === 1 ? 2 : 1,
		} );
	};
	isValid = () => {
		let isValid = true;
		const { AccountNumber } = this.state;
		isValid = isValid && !StringUtil.isEmptyStringOrNull( AccountNumber ) && AccountNumber?.length === 9;
		return isValid;
	};

	render() {
		const { error, dKey } = this.state;
		const frmProps = { holder: StateHolder.buildStateHolder( this ), };
		const isValid = this.isValid();
		return (
			<div key={ dKey } className="row multi-list-add-container">
				<div className="col-md-4">
					<TextInputField
						error={ error }
						maxLength={ 9 }
						valueConverter={ NumericStringConverter }
						id={ "AccountNumber" }
						{ ...frmProps }
						label={ Strings.dewa }
						placeHolder={ Strings.dewa }
					/>
				</div>
				<div className="col-md-4 is-common-container">
					<CheckBoxField
						id={ "isCommon" }
						{ ...frmProps }
						label={ Strings.is_common }
						labelClassName={ "custom-control-label" }
					/>
					<button
						onClick={ this.onAdd }
						disabled={ !isValid }
						type="button" className="btn btn_1">
						<i className="fa fa-plus"></i> { Strings.add }
					</button>
				</div>
			</div>
		);
	}
}

export default PropertyEdit;
